import React, { useEffect, useState, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import ListAltIcon from "@material-ui/icons/ListAlt";
import PeopleIcon from "@material-ui/icons/People";
import VerifiedUserIcon from "@material-ui/icons/VerifiedUser";
import BlockIcon from "@material-ui/icons/Block";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import ContactListProImportWizard from "../ContactListProImportWizard";
import ManualAddContactsModal from "../ManualAddContactsModal";

const emptyLabels = {
  var1: "Variável 1",
  var2: "Variável 2",
  var3: "Variável 3",
  var4: "Variável 4",
  var5: "Variável 5",
  var6: "Variável 6"
};

const OfficialContactListsPanel = ({ onListsChanged }) => {
  const history = useHistory();
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [importOpen, setImportOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [selectedList, setSelectedList] = useState(null);
  const [listDialogOpen, setListDialogOpen] = useState(false);
  const [listForm, setListForm] = useState({ name: "", variableLabels: { ...emptyLabels } });
  const [saving, setSaving] = useState(false);

  const fetchLists = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/official-contact-lists", {
        params: { pageSize: 100, searchParam: search || undefined }
      });
      setLists(Array.isArray(data?.records) ? data.records : []);
      onListsChanged?.();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [onListsChanged, search]);

  useEffect(() => {
    const timer = setTimeout(fetchLists, 250);
    return () => clearTimeout(timer);
  }, [fetchLists]);

  const openListDialog = list => {
    setSelectedList(list || null);
    setListForm({
      name: list?.name || "",
      variableLabels: { ...emptyLabels, ...(list?.variableLabels || {}) }
    });
    setListDialogOpen(true);
  };

  const saveList = async () => {
    if (listForm.name.trim().length < 3) {
      toast.error("Informe um nome com pelo menos 3 caracteres.");
      return;
    }
    setSaving(true);
    try {
      if (selectedList?.id) {
        await api.put(`/official-contact-lists/${selectedList.id}`, listForm);
        toast.success("Lista Meta atualizada com sucesso.");
      } else {
        await api.post("/official-contact-lists", listForm);
        toast.success("Lista Meta criada com sucesso.");
      }
      setListDialogOpen(false);
      await fetchLists();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const deleteList = async list => {
    if (!window.confirm(`Excluir a lista "${list.name}" e todos os seus contatos?`)) return;
    try {
      await api.delete(`/official-contact-lists/${list.id}`);
      toast.success("Lista Meta excluída com sucesso.");
      await fetchLists();
    } catch (err) {
      toastError(err);
    }
  };

  const normalizeList = async list => {
    try {
      const { data } = await api.post(`/official-contact-lists/${list.id}/normalize`);
      toast.success(`${data.updated || 0} número(s) atualizado(s); ${data.invalid || 0} inválido(s).`);
      await fetchLists();
    } catch (err) {
      toastError(err);
    }
  };

  const cleanList = async list => {
    if (!window.confirm(`Excluir os contatos bloqueados, sem opt-in ou inválidos da lista "${list.name}"?`)) return;
    try {
      const { data } = await api.post(`/official-contact-lists/${list.id}/clean`);
      toast.success(data.message || "Limpeza concluída.");
      await fetchLists();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <>
      {importOpen && <ContactListProImportWizard
        open={importOpen}
        onClose={() => {
          setImportOpen(false);
          setSelectedList(null);
        }}
        onSuccess={fetchLists}
        existingListId={selectedList?.id}
        existingListName={selectedList?.name}
        basePath="/official-contact-lists"
        listIdField="officialContactListId"
        dialogTitle="Importar contatos para Lista Meta"
      />}

      {manualOpen && <ManualAddContactsModal
        open={manualOpen}
        onClose={() => {
          setManualOpen(false);
          setSelectedList(null);
        }}
        contactListId={selectedList?.id}
        basePath="/official-contact-lists"
        dialogTitle="Adicionar contatos à Lista Meta"
        onSuccess={fetchLists}
      />}

      {listDialogOpen && <Dialog open={listDialogOpen} onClose={() => setListDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{selectedList ? "Configurar Lista Meta" : "Nova Lista Meta"}</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            label="Nome da lista"
            value={listForm.name}
            onChange={event => {
              const { value } = event.target;
              setListForm(prev => ({ ...prev, name: value }));
            }}
            variant="outlined"
            margin="dense"
          />
          <Typography variant="subtitle2" style={{ marginTop: 18, marginBottom: 8 }}>
            Nomes dos campos personalizados
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
            Use nomes que facilitem o mapeamento, como Boleto, Desconto ou Vencimento.
          </Typography>
          <Grid container spacing={2}>
            {Object.keys(emptyLabels).map((key, index) => (
              <Grid item xs={12} sm={6} key={key}>
                <TextField
                  fullWidth
                  label={`Campo ${index + 1}`}
                  value={listForm.variableLabels[key] || ""}
                  onChange={event => {
                    const { value } = event.target;
                    setListForm(prev => ({
                      ...prev,
                      variableLabels: { ...prev.variableLabels, [key]: value }
                    }));
                  }}
                  variant="outlined"
                  margin="dense"
                />
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setListDialogOpen(false)}>Cancelar</Button>
          <Button color="primary" variant="contained" onClick={saveList} disabled={saving}>
            {saving ? "Salvando..." : "Salvar lista"}
          </Button>
        </DialogActions>
      </Dialog>}

      <Paper style={{ padding: 16, marginBottom: 16 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder="Pesquisar Lista Meta..."
            />
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <Button fullWidth color="primary" variant="outlined" startIcon={<CloudUploadIcon />} onClick={() => {
              setSelectedList(null);
              setImportOpen(true);
            }}>
              Importar nova lista
            </Button>
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <Button fullWidth color="primary" variant="contained" startIcon={<AddIcon />} onClick={() => openListDialog(null)}>
              Nova lista
            </Button>
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <Typography variant="caption" color="textSecondary">
              Prepare os contatos antes de criar a campanha.
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      <Paper variant="outlined" style={{ flex: 1, overflow: "auto" }}>
        {loading && <LinearProgress />}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Lista Meta</TableCell>
              <TableCell align="center">Contatos</TableCell>
              <TableCell align="center">Prontos</TableCell>
              <TableCell align="center">Bloqueados</TableCell>
              <TableCell align="center">Situação</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lists.map(list => (
              <TableRow key={list.id}>
                <TableCell>
                  <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                    <PeopleIcon color="primary" />
                    <Box>
                      <Typography variant="body2">{list.name}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        Criada em {new Date(list.createdAt).toLocaleDateString("pt-BR")}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell align="center">{list.contactsCount || 0}</TableCell>
                <TableCell align="center">{list.validCount || 0}</TableCell>
                <TableCell align="center">{list.blockedCount || 0}</TableCell>
                <TableCell align="center">
                  <Chip
                    size="small"
                    color={list.ready ? "primary" : "default"}
                    label={list.ready ? "Pronta para campanha" : "Revisar contatos"}
                  />
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Adicionar contatos"><IconButton size="small" onClick={() => { setSelectedList(list); setManualOpen(true); }}><AddIcon /></IconButton></Tooltip>
                  <Tooltip title="Importar ou atualizar"><IconButton size="small" onClick={() => { setSelectedList(list); setImportOpen(true); }}><CloudUploadIcon /></IconButton></Tooltip>
                  <Tooltip title="Ver e editar contatos"><IconButton size="small" onClick={() => history.push(`/official-campaigns/contact-lists/${list.id}`, { contactList: list })}><ListAltIcon /></IconButton></Tooltip>
                  <Tooltip title="Configurar nomes das variáveis"><IconButton size="small" onClick={() => openListDialog(list)}><EditIcon /></IconButton></Tooltip>
                  <Tooltip title="Atualizar padrão dos números"><IconButton size="small" onClick={() => normalizeList(list)}><VerifiedUserIcon /></IconButton></Tooltip>
                  <Tooltip title="Excluir bloqueados e inválidos"><IconButton size="small" onClick={() => cleanList(list)}><BlockIcon /></IconButton></Tooltip>
                  <Tooltip title="Excluir lista"><IconButton size="small" onClick={() => deleteList(list)}><DeleteOutlineIcon /></IconButton></Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {!loading && lists.length === 0 && (
          <Box py={6} textAlign="center">
            <Typography color="textSecondary">Nenhuma Lista Meta cadastrada.</Typography>
          </Box>
        )}
      </Paper>
    </>
  );
};

export default OfficialContactListsPanel;
